/**
 * AGC: BattleCenter Component - Reusable battle center section with hero/element selection
 * AGC: Displays origami choice carousel, element carousel, and submit turn button
 * AGC: Includes potion selection (one-time use per game, player chooses when to use)
 * AGC: Used in HybridPvEBattle, InteractiveP2PBattle, and other battle components
 * 
 * AGC: ⚠️ IMPORTANT - Potions vs Buffs:
 * AGC: - BUFFS: Passive, present all turns, trigger by RNG luck
 * AGC: - POTIONS: Active, ONE-TIME use, player chooses when, effect is GUARANTEED
 */

import React, { useCallback, useRef, useState, useEffect } from 'react'
import { SwipeCarousel } from '../common/SwipeCarousel'
import { unlockAudioPlayback, playSoundSafe } from '../../utils/audio'

export type Choice = 'rock' | 'paper' | 'scissors' | 'ROCK' | 'PAPER' | 'SCISSORS'
export type Element = 'water' | 'fire' | 'ice' | 'wind' | 'WATER' | 'FIRE' | 'ICE' | 'WIND' | 'NONE'

// AGC: Potion type helpers for UI display
const POTION_TYPES = ['Healing', 'Mana', 'Double Strike', 'Poison'] as const
// AGC: Potion image mapping: red=healing, blue=mana, purple=double strike, green=poison
const POTION_IMAGES = ['/potion_red.png', '/potion_blue.png', '/potion_purple.png', '/potion_green.png'] as const
const RARITY_NAMES = ['Common', 'Uncommon', 'Rare', 'Epic', 'Legendary'] as const
const RARITY_COLORS = [
  'text-slate-300', // Common
  'text-green-400', // Uncommon
  'text-blue-400',  // Rare
  'text-purple-400', // Epic
  'text-orange-400'  // Legendary
] as const

// AGC: Healing potion energy values by rarity
// AGC: UPDATED 2025-11-27 - New multipliers: Epic 1.50× (was 1.30×), Legendary 2.00× (was 1.50×)
const HEALING_ENERGY = [10, 11, 12, 15, 20] as const // Common=10, Uncommon=11, Rare=12, Epic=15, Legendary=20

/**
 * AGC: Get potion display info for UI
 */
const getPotionInfo = (potionType: number, rarity: number) => {
  const typeName = POTION_TYPES[potionType] || 'Unknown'
  const imagePath = POTION_IMAGES[potionType] || '/potion_red.png'
  const rarityName = RARITY_NAMES[rarity] || 'Unknown'
  const rarityColor = RARITY_COLORS[rarity] || 'text-slate-300'
  
  // AGC: Effect description based on type and rarity
  // AGC: UPDATED 2025-11-27 - New multipliers: Epic 1.50× (was 1.30×), Legendary 2.00× (was 1.50×)
  let effectDesc = ''
  switch (potionType) {
    case 0: // Healing - INSTANT energy restore
      // AGC: Base 10 energy × rarity multiplier (1.00, 1.10, 1.20, 1.50, 2.00)
      const healingEnergy = [10, 11, 12, 15, 20][rarity] || 10
      effectDesc = `+${healingEnergy} Energy (Instant)`
      break
    case 1: // Mana - PERSISTENT for ALL remaining turns
      // AGC: Base 5% × rarity multiplier (1.00, 1.10, 1.20, 1.50, 2.00)
      const manaBoost = [5, 5.5, 6, 7.5, 10][rarity] || 5
      effectDesc = `+${manaBoost}% Buff Power (All Turns)`
      break
    case 2: // Double Strike - ONLY activates when you WIN that turn
      // AGC: Base 25% × rarity multiplier (1.00, 1.10, 1.20, 1.50, 2.00)
      const dmgBoost = [25, 27.5, 30, 37.5, 50][rarity] || 25
      effectDesc = `+${dmgBoost}% Damage (Win Only)`
      break
    case 3: // Poison
      // AGC: Base 2.5 damage × rarity multiplier (1.00, 1.10, 1.20, 1.50, 2.00)
      const poisonDmg = [2.5, 2.75, 3.0, 3.75, 5.0][rarity] || 2.5
      effectDesc = `${poisonDmg} Dmg/Turn × 5 Turns`
      break
    default:
      effectDesc = 'Unknown Effect'
  }
  
  return { typeName, imagePath, rarityName, rarityColor, effectDesc }
}

export interface BattleCenterProps {
  // AGC: Battle state
  turnSubmitted: boolean
  waitingForOpponent: boolean
  battleEnded: boolean
  turnTimeLeft: number
  
  // AGC: Selection state
  selectedChoice: Choice
  selectedElement: Element
  onChoiceSelect: (choice: Choice) => void
  onElementSelect: (element: Element) => void
  
  // AGC: Submit handler
  onSubmitTurn: () => void
  
  // AGC: Helper functions
  getChoiceImage: (choice: Choice | string) => string
  getElementEmoji: (element: Element | string) => string
  
  // AGC: Options
  origamiOptions: Choice[]
  elementOptions: Element[]
  
  // AGC: Potion selection (ONE-TIME USE - player chooses when to activate)
  // AGC: Unlike buffs (passive RNG), potions are active and GUARANTEED to work
  onPotionSelect?: (potionId: number | null) => void
  selectedPotion?: number | null // AGC: Which potion is selected to USE THIS TURN
  availablePotions?: Array<{ 
    tokenId: number
    potionType: number // 0=Healing, 1=Mana, 2=DoubleStrike, 3=Poison
    rarity: number // 0=Common, 1=Uncommon, 2=Rare, 3=Epic, 4=Legendary
    magnitude: number // Effect strength based on rarity
  }>
  potionUsed?: boolean // AGC: True if potion already used this game (disabled forever)
  equippedPotion?: { // AGC: The potion player brought to this battle
    tokenId: number
    potionType: number
    rarity: number
    magnitude: number
  } | null
}

/**
 * AGC: BattleCenter - Reusable battle center component
 * AGC: Contains hero/element selectors and submit button
 */
export const BattleCenter: React.FC<BattleCenterProps> = ({
  turnSubmitted,
  waitingForOpponent,
  battleEnded,
  turnTimeLeft: _turnTimeLeft,
  selectedChoice,
  selectedElement,
  onChoiceSelect,
  onElementSelect,
  onSubmitTurn,
  getChoiceImage,
  getElementEmoji,
  origamiOptions,
  elementOptions,
  onPotionSelect,
  selectedPotion,
  availablePotions: _availablePotions = [], // AGC: Available for future use
  potionUsed = false,
  equippedPotion = null
}) => {
  // AGC: State for potion tooltip/info display
  const [showPotionInfo, setShowPotionInfo] = useState(false)
  const submitButtonRef = useRef<HTMLButtonElement | null>(null)
  const [highlightSubmitButton, setHighlightSubmitButton] = useState(false)
  
  // AGC: Remember last selection for next turn
  const lastSelectionRef = useRef<{ choice: Choice; element: Element } | null>(null)
  // AGC: Track if we've already restored selection for this turn (prevents re-restoring when user navigates)
  const hasRestoredRef = useRef(false)

  // AGC: Carousel indices
  const defaultChoiceIndex = 1
  const defaultElementIndex = 1
  const [choiceCarouselIndex, setChoiceCarouselIndex] = useState(
    origamiOptions.indexOf(selectedChoice) >= 0 ? origamiOptions.indexOf(selectedChoice) : defaultChoiceIndex
  )
  const [elementCarouselIndex, setElementCarouselIndex] = useState(
    elementOptions.indexOf(selectedElement) >= 0 ? elementOptions.indexOf(selectedElement) : defaultElementIndex
  )
  
  // AGC: Reset restore flag when turn is submitted (new turn will start)
  useEffect(() => {
    if (turnSubmitted) {
      hasRestoredRef.current = false
    }
  }, [turnSubmitted])
  
  // AGC: Restore last selection when new turn starts (only once per turn)
  useEffect(() => {
    // AGC: Only restore if turn just started (not submitted, not waiting) and we haven't restored yet
    if (!turnSubmitted && !waitingForOpponent && lastSelectionRef.current && !hasRestoredRef.current) {
      const { choice, element } = lastSelectionRef.current
      // AGC: Restore selection if valid
      if (origamiOptions.includes(choice)) {
        const choiceIndex = origamiOptions.indexOf(choice)
        setChoiceCarouselIndex(choiceIndex)
        onChoiceSelect(choice)
      }
      if (elementOptions.includes(element)) {
        const elementIndex = elementOptions.indexOf(element)
        setElementCarouselIndex(elementIndex)
        onElementSelect(element)
      }
      // AGC: Mark as restored so we don't restore again when user navigates
      hasRestoredRef.current = true
    }
  }, [turnSubmitted, waitingForOpponent, origamiOptions, elementOptions, onChoiceSelect, onElementSelect])
  
  // AGC: Save selection when turn is submitted
  useEffect(() => {
    if (turnSubmitted) {
      lastSelectionRef.current = {
        choice: selectedChoice,
        element: selectedElement
      }
    }
  }, [turnSubmitted, selectedChoice, selectedElement])

  // AGC: Focus submit button helper
  const focusSubmitButton = useCallback(() => {
    if (!submitButtonRef.current) return
    setHighlightSubmitButton(true)
  }, [])

  // AGC: Clear highlight after delay
  useEffect(() => {
    if (!highlightSubmitButton) return
    const timeoutId = setTimeout(() => setHighlightSubmitButton(false), 3000)
    return () => clearTimeout(timeoutId)
  }, [highlightSubmitButton])

  // AGC: Handle choice selection
  const handleChoiceSelect = useCallback(
    (index: number) => {
      setChoiceCarouselIndex(index)
      const choice = origamiOptions[index]
      // AGC: Play hero selection sound
      playSoundSafe('/sounds/click_hero.wav', 0.3)
      onChoiceSelect(choice)
      focusSubmitButton()
    },
    [origamiOptions, onChoiceSelect, focusSubmitButton]
  )

  // AGC: Handle element selection
  const handleElementSelect = useCallback(
    (index: number) => {
      setElementCarouselIndex(index)
      const element = elementOptions[index]
      // AGC: Play element selection sound
      playSoundSafe('/sounds/click_hero.wav', 0.3)
      onElementSelect(element)
      focusSubmitButton()
    },
    [elementOptions, onElementSelect, focusSubmitButton]
  )

  if (battleEnded || turnSubmitted) {
    return null
  }

  return (
    <div className="pb-2 border-b border-white/10 flex flex-col relative">
      {/* AGC: Potion Selection - One-time use, player chooses when to activate */}
      {/* AGC: Unlike buffs (passive RNG), potions are ACTIVE and GUARANTEED */}
      {onPotionSelect && equippedPotion && (
        <div className="absolute top-2 left-2 z-10">
          {/* AGC: Main potion button */}
          <div className="relative">
            <button
              type="button"
              data-tour="simulation-potion-button"
              onClick={() => {
                // AGC: Simple toggle - must not be used/submitted/waiting
                if (potionUsed || turnSubmitted || waitingForOpponent) return
                
                // AGC: Play click sound
                playSoundSafe('/sounds/click.mp3', 0.3)
                
                // AGC: Simple toggle: selected -> deselect, not selected -> select
                const isCurrentlySelected = selectedPotion === equippedPotion.tokenId
                onPotionSelect(isCurrentlySelected ? null : equippedPotion.tokenId)
              }}
              onMouseEnter={() => setShowPotionInfo(true)}
              onMouseLeave={() => setShowPotionInfo(false)}
              disabled={potionUsed || turnSubmitted || waitingForOpponent}
              className={`w-14 h-14 rounded-lg border-2 transition-all flex flex-col items-center justify-center text-lg relative ${
                selectedPotion === equippedPotion.tokenId && !potionUsed
                  ? 'bg-gradient-to-br from-green-600/90 to-emerald-600/90 border-green-400 text-white shadow-lg shadow-green-500/50 animate-pulse'
                  : potionUsed
                    ? 'bg-slate-800/80 border-slate-600 text-slate-500 cursor-not-allowed opacity-60'
                    : 'bg-gradient-to-br from-purple-800/60 to-pink-800/60 border-purple-500/70 text-slate-200 hover:from-purple-700/70 hover:to-pink-700/70 hover:border-purple-400'
              }`}
              title={
                potionUsed 
                  ? 'Potion Already Used This Game' 
                  : selectedPotion === equippedPotion.tokenId 
                    ? 'Click to Cancel - Potion Will Be Used This Turn!' 
                    : 'Click to Use Potion This Turn'
              }
            >
              {/* AGC: Potion icon */}
              {potionUsed ? (
                <span className="text-xl">✅</span>
              ) : (
                <img 
                  src={POTION_IMAGES[equippedPotion.potionType] || '/potion_red.png'} 
                  alt={POTION_TYPES[equippedPotion.potionType] || 'Potion'}
                  className="w-8 h-8 object-contain"
                />
              )}
              {/* AGC: Status indicator */}
              <span className="text-[7px] uppercase tracking-wider mt-0.5">
                {potionUsed 
                  ? 'Used' 
                  : selectedPotion === equippedPotion.tokenId 
                    ? 'Active!' 
                    : 'Ready'}
              </span>
              
              {/* AGC: Selection indicator - green checkmark when selected for this turn */}
              {selectedPotion === equippedPotion.tokenId && !potionUsed && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold shadow-lg border-2 border-green-300 animate-bounce">
                  ✓
                </span>
              )}
            </button>
            
            {/* AGC: Tooltip showing potion info */}
            {showPotionInfo && !potionUsed && (
              <div className="absolute left-0 top-full mt-2 w-48 p-2 bg-slate-900/95 border border-slate-600 rounded-lg shadow-xl z-20">
                {(() => {
                  const info = getPotionInfo(equippedPotion.potionType, equippedPotion.rarity)
                  return (
                    <>
                      <div className="flex items-center gap-2 mb-1">
                        <img 
                          src={info.imagePath} 
                          alt={info.typeName}
                          className="w-6 h-6 object-contain"
                        />
                        <span className={`text-sm font-bold ${info.rarityColor}`}>{info.rarityName}</span>
                      </div>
                      <div className="text-xs text-slate-300 font-medium">{info.typeName} Potion</div>
                      <div className="text-xs text-emerald-400 mt-1">{info.effectDesc}</div>
                      <div className="text-[9px] text-slate-500 mt-2 border-t border-slate-700 pt-1">
                        {selectedPotion === equippedPotion.tokenId 
                          ? '⚡ Will activate when you Submit Turn!'
                          : '👆 Click to use this turn (1 use per game)'}
                      </div>
                    </>
                  )
                })()}
              </div>
            )}
          </div>
          
          {/* AGC: "Use This Turn" label when selected */}
          {selectedPotion === equippedPotion.tokenId && !potionUsed && (
            <div className="absolute left-16 top-1/2 -translate-y-1/2 whitespace-nowrap">
              <span className="text-[10px] text-green-400 font-bold uppercase tracking-wider bg-green-900/50 px-2 py-1 rounded border border-green-500/50">
                ⚡ Using This Turn!
              </span>
            </div>
          )}
        </div>
      )}
      
      {/* AGC: Empty potion slot indicator (no potion equipped) - shows disabled green potion */}
      {onPotionSelect && !equippedPotion && (
        <div className="absolute top-2 left-2 z-10">
          <div className="relative">
            <button
              type="button"
              onClick={() => {
                // AGC: Show message when user tries to click disabled potion
                alert('No potions available')
              }}
              disabled
              className="w-14 h-14 rounded-lg border-2 border-slate-600/50 bg-slate-800/60 cursor-not-allowed flex flex-col items-center justify-center relative overflow-hidden"
              title="No potions available"
            >
              {/* AGC: Green potion image (base image) */}
              <img 
                src="/potion_green.png" 
                alt="No Potion"
                className="w-8 h-8 object-contain"
              />
              
              {/* AGC: Gray overlay to show disabled state */}
              <div className="absolute inset-0 bg-slate-900/70 rounded-lg" />
              
              {/* AGC: Status text */}
              <span className="text-[7px] uppercase tracking-wider mt-0.5 text-slate-500 relative z-10">
                Empty
              </span>
            </button>
          </div>
        </div>
      )}
      
      <div className="flex-1 flex flex-col justify-center space-y-2">
        {/* AGC: Origami Choice Carousel - 10% smaller, picture 80% of card */}
        <div data-tour="simulation-hero-selection">
          <h4 className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1">Choose Origami</h4>
          <SwipeCarousel
            items={origamiOptions}
            selectedIndex={choiceCarouselIndex}
            onSelect={handleChoiceSelect}
            size="mediumMinus"
            loop
            renderItem={({ item, isActive, isSelected, select }) => (
              <button
                type="button"
                onClick={select}
                className={`group relative flex h-full w-full select-none flex-col overflow-hidden rounded-lg border transition-all duration-300 ease-out ${
                  isSelected
                    ? 'border-blue-400'
                    : isActive
                      ? 'border-blue-300/70'
                      : 'border-slate-500/60 opacity-80'
                }`}
              >
                {/* AGC: Removed card background - following improve_design.md */}
                <div className="relative flex h-full w-full flex-col items-center justify-center">
                  <div className="flex flex-col items-center justify-center w-[80%] h-[80%]">
                    <img
                      src={getChoiceImage(item)}
                      alt={item}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-[8px] uppercase tracking-[0.25em] text-white absolute bottom-1">{item}</p>
                  {selectedChoice === item && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-blue-500/90 text-white flex items-center justify-center text-[10px] font-bold shadow-lg border border-blue-300/50 z-10">
                      ✓
                    </span>
                  )}
                </div>
              </button>
            )}
          />
        </div>

        {/* AGC: Element Choice Carousel - 10% bigger, icon 90% of card */}
        <div data-tour="simulation-element-selection">
          <h4 className="text-[9px] uppercase tracking-[0.25em] text-slate-500 mb-1">Choose Element</h4>
          <SwipeCarousel
            items={elementOptions}
            selectedIndex={elementCarouselIndex}
            onSelect={handleElementSelect}
            size="tinyPlus"
            loop
            renderItem={({ item, isActive, isSelected, select }) => {
              const palette =
                item === 'fire'
                  ? 'from-rose-900/70 via-orange-700/60 to-amber-500/40'
                  : item === 'water'
                    ? 'from-cyan-900/70 via-blue-700/60 to-sky-500/40'
                    : item === 'ice'
                      ? 'from-slate-900/70 via-sky-600/50 to-slate-200/40'
                      : 'from-emerald-900/70 via-lime-700/50 to-emerald-400/40'

              return (
                <button
                  type="button"
                  onClick={select}
                  className={`relative flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-lg border bg-gradient-to-br transition-all duration-300 ease-out ${
                    isSelected
                      ? 'border-amber-300'
                      : isActive
                        ? 'border-amber-200/70'
                        : 'border-slate-500/60 opacity-80'
                  } ${palette}`}
                >
                  {/* AGC: Removed bg-black/25 overlay - following improve_design.md */}
                  {/* AGC: Center icon vertically from top to dots (bottom-3 + dot height ≈ 24px) */}
                  {/* AGC: Using PNG images instead of emojis */}
                  <div className="relative flex h-full w-full flex-col items-center justify-center pb-6">
                    <img 
                      src={(() => {
                        const normalizedElem = item?.toLowerCase()
                        switch (normalizedElem) {
                          case 'water': return '/water.png'
                          case 'fire': return '/fire.png'
                          case 'ice': return '/ice.png'
                          case 'wind': return '/wind.png'
                          default: return '/water.png'
                        }
                      })()} 
                      alt={item} 
                      className="w-12 h-12 object-contain drop-shadow-lg"
                    />
                  </div>
                  {/* AGC: Checkmark for selected element */}
                  {selectedElement === item && (
                    <span className="absolute top-1 right-1 w-4 h-4 rounded-full bg-amber-500/90 text-white flex items-center justify-center text-[10px] font-bold shadow-lg border border-amber-300/50 z-10">
                      ✓
                    </span>
                  )}
                </button>
              )
            }}
          />
        </div>

      </div>

      {/* AGC: Submit Turn Button */}
      <div className="mt-3">
        <button
          ref={submitButtonRef}
          data-tour="simulation-submit-turn"
          onClick={() => {
            unlockAudioPlayback()
            playSoundSafe('/sounds/click.mp3', 0.4)
            onSubmitTurn()
          }}
          disabled={turnSubmitted || waitingForOpponent || battleEnded}
          className={`w-full rounded-xl py-3 px-6 text-base font-bold text-white shadow-xl transition-all duration-200 disabled:cursor-not-allowed disabled:from-slate-700 disabled:to-slate-800 disabled:opacity-70 ${
            // AGC: Different gradient when potion is selected for this turn
            selectedPotion && equippedPotion && !potionUsed
              ? 'bg-gradient-to-r from-purple-600 via-green-600 to-emerald-600 hover:from-purple-500 hover:via-green-500 hover:to-emerald-500'
              : 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500'
          } ${
            highlightSubmitButton ? 'ring-4 ring-amber-400 ring-offset-2 ring-offset-slate-900 animate-pulse' : ''
          }`}
        >
          {turnSubmitted || waitingForOpponent 
            ? '⏳ Waiting...' 
            : selectedPotion && equippedPotion && !potionUsed
              ? `⚡ Submit + Use ${POTION_TYPES[equippedPotion.potionType] || 'Potion'}!`
              : '🔥 Submit Turn'}
        </button>
        
        {/* AGC: Potion usage hint below button */}
        {selectedPotion && equippedPotion && !potionUsed && !turnSubmitted && (
          <div className="mt-2 text-center">
            <span className="text-[10px] text-green-400/80">
              {(() => {
                const info = getPotionInfo(equippedPotion.potionType, equippedPotion.rarity)
                return (
                  <span className="flex items-center gap-1">
                    <img src={info.imagePath} alt={info.typeName} className="w-3 h-3 object-contain" />
                    <span>{info.effectDesc} will apply immediately!</span>
                  </span>
                )
              })()}
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

